import {
  Button,
  Card,
  DataTable,
  Group,
  Tag,
  Title,
  Tooltip,
} from "components/common";
import { ActionButtons, Filters, TagsWithPlus } from "components/custom";
import { SCRIPT_EDIT_ICON } from "components/svg";
import {
  SCRIPT_ADD_QUESTIONS,
  SCRIPT_EDIT_QUESTIONS,
  SCRIPT_VIEW_QUESTIONS,
} from "constants/app-paths";
import { canUserModify } from "features/presentation/utils";
import FeedbackModal from "features/quiz/takeQuiz/feedbackModal";
import {
  useGetPageTopicsQuery,
  useGetSearchListingScriptTopicsQuery,
  useGetSpecialitiesQuery,
  useUpdateDifficultyLevelMutation,
  useUpdateQuestionStatsMutation,
} from "features/script/api";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import theme from "utils/themeVars";
import { useGetAllQuestionsQuery } from "../apis";
import PageLayout from "components/common/pageLayout/pageLayout";

const multipleModes = {
  specialties: true,
  scripts: true,
  topics: true,
  specialty: true,
  speciality: true,
  level: true,
};

const getPlainText = (html = "") =>
  html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const truncateText = (text, length = 90) =>
  text.length > length ? `${text.slice(0, length)}...` : text;

function processObject(inputObj) {
  // Filter out keys with empty or undefined values
  const filteredObj = Object.fromEntries(
    Object.entries(inputObj).filter(
      ([_, value]) => value !== undefined && value !== ""
    )
  );

  // Convert comma-separated values to arrays
  const processedObj = Object.fromEntries(
    Object.entries(filteredObj).map(([key, value]) => {
      return [key, multipleModes?.[key] ? value.split(",") : value];
    })
  );

  return processedObj;
}

const QuestionsListing = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  let queryObj = Object.fromEntries([...searchParams]);
  const [showModal, setShowModal] = useState(false);
  const [selectionQuestion, setSelectionQuestion] = useState(false);
  const [loading, setLoading] = useState(null);

  const user = useSelector((state) => state.app.user?.info);

  const getUserId = (queryObj) => {
    const keysToCheck = ["upVoted", "downVoted", "bookmarked"];
    if (keysToCheck.some((key) => queryObj.hasOwnProperty(key))) {
      return { userId: user?.user_code };
    }
  };

  const [handleUpdateStats, _] = useUpdateQuestionStatsMutation();

  const {
    data: allQuestions,
    isLoading: isLoadingQuestions,
    isFetching: isFetchingQuestions,
    error: questionsError,
    refetch: refetchQuestions,
  } = useGetAllQuestionsQuery(
    {
      ...processObject(queryObj),
      ...(queryObj?.page && { pageNum: queryObj?.page }),
      ...(queryObj?.per_page && { pageSize: queryObj?.per_page }),
      ...getUserId(queryObj),
    },
    { refetchOnMountOrArgChange: true }
  );

  const {
    data: allScripts,
    isLoading: isLoadingScripts,
    isFetching: isFetchingScripts,
    error: scriptsError,
    refetch: refetchScripts,
  } = useGetPageTopicsQuery("");

  const { data: allTopics } = useGetSearchListingScriptTopicsQuery();
  const { data: specialties } = useGetSpecialitiesQuery();

  const onUpdateStats = async (question, options, key) => {
    setLoading({ id: question?.questionId, key: key });
    const res = await handleUpdateStats({
      questionId: question?.questionId,
      scriptId: question?.scripts?.[0],
      options: options,
      action: key,
    });
    if (res) {
      setLoading(null);
    }
  };

  const findScript = (scripts) => {
    if (allScripts?.length && scripts?.length) {
      return allScripts
        .filter(
          (e) =>
            scripts.includes(e.editor_template_id) || scripts?.includes(e?._id)
        )
        ?.map((e) => e?.page_name);
    }
    return ["-"];
  };

  const findTopic = (topics) => {
    if (allTopics?.data?.length && topics?.length) {
      return allTopics?.data
        .filter((topic) => topics.includes(topic._id))
        ?.map((e) => e?.PageTopics);
    }
    return ["-"];
  };

  const columns = [
    {
      title: "No.",
      dataIndex: "key",
      className: "tableStartSpace",
      width: 100,
    },
    // {
    //   title: "Template Type",
    //   dataIndex: "templateType",
    // },
    // {
    //   title: "Specialty",
    //   dataIndex: "specialty",
    // },
    {
      title: "Question ID",
      dataIndex: "question_id",
    },
    {
      title: "Question",
      dataIndex: "description",
    },
    {
      title: "Speciality",
      dataIndex: "speciality",
    },
    {
      title: "Scripts",
      dataIndex: "script",
    },
    {
      title: "explanation",
      dataIndex: "explanation",
    },
    {
      title: "Topics",
      dataIndex: "topics",
    },
    {
      title: "Actions",
      dataIndex: "actions",
    },
  ];

  const checkTrue = (data, userId, key) => {
    if (data?.[key]?.length && data?.[key]?.includes(userId)) {
      return true;
    }
    return false;
  };

  const onToggleBookmark = (e) => {
    let isSaved = [];
    if (e?.questionDetails?.isSaved?.length) {
      isSaved = e?.questionDetails?.isSaved;
    }
    if (isSaved?.length && isSaved?.includes(user?.user_code)) {
      isSaved = isSaved.filter((id) => id != user?.user_code);
    } else {
      isSaved.push(user?.user_code);
    }
    onUpdateStats(e, { ...e?.questionDetails, isSaved: isSaved }, "saved");
  };

  const onToggleLike = (e) => {
    let disLiked = JSON.parse(JSON.stringify(e?.questionDetails?.disLiked));
    let liked = JSON.parse(JSON.stringify(e?.questionDetails?.liked));
    if (liked?.length && liked?.includes(user?.user_code)) {
      liked = liked.filter((id) => id != user?.user_code);
    } else {
      liked.push(user?.user_code);
      if (disLiked?.length && disLiked?.includes(user?.user_code)) {
        disLiked = disLiked.filter((id) => id != user?.user_code);
      }
    }
    onUpdateStats(e, { ...e?.questionDetails, disLiked, liked }, "liked");
  };

  const onToggleDislike = (e) => {
    let disLiked = JSON.parse(JSON.stringify(e?.questionDetails?.disLiked));
    let liked = JSON.parse(JSON.stringify(e?.questionDetails?.liked));
    if (disLiked?.length && disLiked?.includes(user?.user_code)) {
      disLiked = disLiked.filter((id) => id != user?.user_code);
    } else {
      disLiked.push(user?.user_code);
      if (liked?.length && liked?.includes(user?.user_code)) {
        liked = liked.filter((id) => id != user?.user_code);
      }
    }
    onUpdateStats(e, { ...e?.questionDetails, disLiked, liked }, "disLiked");
  };

  const getActionsList = (e) => {
    const actionColor = canUserModify(user, e?.editor_id)
      ? theme["blue200"]
      : theme["gray500"];
    const isSaved = checkTrue(e?.questionDetails, user?.user_code, "isSaved");
    const isLiked = checkTrue(e?.questionDetails, user?.user_code, "liked");
    const isDisliked = checkTrue(e?.questionDetails, user?.user_code, "disLiked");

    return [
      {
        key: "edit_question",
        icon: <SCRIPT_EDIT_ICON />,
        color: actionColor,
        content: "Edit Question",
        title: "Edit Question",
        className: "pointer",
        onClick: () => {
          navigate(SCRIPT_EDIT_QUESTIONS.replace(":id", e?.questionId));
        },
      },
      {
        key: "more",
        icon: "FiMoreHorizontal",
        color: actionColor,
        content: "More",
        className: "pointer",
        options: [
          {
            id: "bookmark",
            label: isSaved ? "Remove Bookmark" : "Bookmark",
            icon: isSaved ? "FaBookmark" : "LuBookmark",
            onClick: () => onToggleBookmark(e),
          },
          {
            id: "like",
            label: isLiked ? "Unlike" : "Like",
            icon: isLiked ? "AiFillLike" : "AiOutlineLike",
            onClick: () => onToggleLike(e),
          },
          {
            id: "dislike",
            label: isDisliked ? "Remove Dislike" : "Dislike",
            icon: isDisliked ? "AiFillDislike" : "AiOutlineDislike",
            onClick: () => onToggleDislike(e),
          },
          {
            id: "feedback",
            label: "Add Feedback",
            icon: "MdFeedback",
            onClick: () => {
              setSelectionQuestion(e);
              setShowModal(true);
            },
          },
        ],
      },
    ];
  };

  const findDifficultyLevel = (item) => {
    const difficultyConfig = {
      easy: {
        label: "Easy",
        color: "#4ca64c",
      },
      medium: {
        label: "Medium",
        color: theme["warning-color"],
      },
      hard: {
        label: "Hard",
        color: theme["error-color"],
      },
    };

    const levels = Object.keys(difficultyConfig);
    for (const level of levels) {
      const userList = item?.questionDetails?.difficultyLevels?.[level];
      if (userList?.length && userList.includes(user?.user_code)) {
        return difficultyConfig[level];
      }
    }
    return null;
  };

  const tableData = useCallback(() => {
    return allQuestions?.data?.length
      ? allQuestions?.data?.map((e, index) => ({
        ...e,
        question_id: (
          <div style={{ display: "flex", gap: "10px" }}>
            <div>{e?.questionId}</div>
            {findDifficultyLevel(e)?.label && (
              <Tag
                key={index}
                style={{ marginBottom: "0.5vh" }}
                color={findDifficultyLevel(e)?.color}
              >
                {findDifficultyLevel(e)?.label}
              </Tag>
            )}
          </div>
        ),
        key: index + 1,
        description: (() => {
          const plain = getPlainText(e?.description || "");
          return plain ? (
            <Tooltip title={plain}>
              <span style={{ display: "inline-block", maxWidth: 360 }}>
                {truncateText(plain)}
              </span>
            </Tooltip>
          ) : (
            "-"
          );
        })(),
        speciality: (
          <TagsWithPlus
            data={
              e?.speciality?.length
                ? e.speciality.map((item) => item?.name).filter(Boolean)
                : ["-"]
            }
            plusAfter={3}
          />
        ),
        script: <TagsWithPlus data={findScript(e?.scripts)} plusAfter={3} />,
        explanation: (
          <TagsWithPlus
            data={e?.explanation?.map((e) => e?.title)}
            plusAfter={3}
          />
        ),
        topics: <TagsWithPlus data={findTopic(e?.topics)} plusAfter={3} />,
        actions: (
          <div onClick={(ev) => ev.stopPropagation()}>
            <ActionButtons list={getActionsList(e)} />
          </div>
        ),
      }))
      : [];
  }, [allQuestions, allScripts, allTopics]);

  const filterList = useMemo(
    () => [
      {
        label: "none",
        type: "unitSelect",
        key: "search",
        keyList: [
          { label: "Question ID", value: "questionIid" },
          { label: "Keyword", value: "searchedString" },
        ],
      },
      {
        label: "none",
        list: allScripts?.length
          ? allScripts.map((e) => ({
            label: e?.page_name,
            value: e?.editor_template_id,
          }))
          : [],
        type: "select",
        key: "scripts",
        placeholder: "Select Script",
        mode: "multiple",
      },
      {
        label: "none",
        list: specialties?.data?.length
          ? specialties.data.map((spec) => ({
            label: spec?.topic,
            value: spec?._id,
          }))
          : [],
        type: "select",
        key: "speciality",
        placeholder: "Select Specialty",
        mode: "multiple",
      },
      {
        label: "none",
        list: allTopics?.data?.length
          ? allTopics?.data?.map((e) => ({
            ...e,
            label: e?.PageTopics,
            value: e?._id,
          }))
          : [],
        type: "select",
        key: "topics",
        placeholder: "Select Topics",
      },
      {
        label: "none",
        list: [
          { label: "Easy", value: "easy" },
          { label: "Medium", value: "medium" },
          { label: "Hard", value: "Hard" },
        ],
        type: "select",
        key: "level",
        placeholder: "Select Difficulty",
        mode: "multiple",
      },
      // {
      //   label: "none",
      //   list: [],
      //   type: "select",
      //   key: "users",
      //   placeholder: "Select Users",
      // },
      {
        key: "bookmarked",
        label: "Bookmarked",
        type: "toggle",
      },
      {
        key: "upVoted",
        label: "UpVoted",
        type: "toggle",
      },
      {
        key: "downVoted",
        label: "DownVoted",
        type: "toggle",
      },
    ],
    [allScripts, allTopics?.data, specialties?.data]
  );

  const handleAddFeedback = async (data) => {
    // const res = await handleUpdateStats({
    //   questionId: question?.questionId,
    //   scriptId: scriptId,
    //   options: options,
    //   action: key,
    // });
  };

  return (
    <PageLayout showSubMenu={true}>
      <Group
        gap="16px"
        className={"listings-content-adjustments"}
        style={{ marginTop: "-20px" }}
      >
        <FeedbackModal
          question={selectionQuestion}
          showModal={showModal}
          setShowModal={setShowModal}
          onSubmit={handleAddFeedback}
        />
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Filters list={filterList || []} />
          </div>
          <Button
            icon="AiOutlinePlus"
            text="Add Question"
            type="light"
            color="#1677FF"
            style={{
              backgroundColor: "#f0f8ff",
              borderColor: "#d6e4ff",
              color: "#1677FF",
              height: 48,
              fontWeight: 700,
            }}
            onClick={() => {
              navigate(SCRIPT_ADD_QUESTIONS);
            }}
          />
        </div>
        <DataTable
          pagination={{
            totalCount: allQuestions?.totalCount,
            pageOptions: [10, 25, 50],
          }}
          paginationOnBottom={true}
          onChangePage={(queryObj) => setSearchParams(queryObj)}
          columns={columns}
          data={tableData()}
          stickyOffset={0}
          rowClassName={() => "pointer"}
          onRow={(record) => ({
            onClick: () =>
              navigate(
                SCRIPT_VIEW_QUESTIONS.replace(":id", record?.questionId)
              ),
          })}
          loading={isLoadingQuestions || isLoadingScripts}
          error={questionsError || scriptsError}
          onErrorRetry={() => {
            if (questionsError) refetchQuestions();
            if (scriptsError) refetchScripts();
          }}
          skeletonLoading={isFetchingQuestions || isFetchingScripts}
        />
      </Group>
    </PageLayout>
  );
};

export default QuestionsListing;
